import React, {
    ChangeEvent,
    FocusEvent,
    HTMLAttributes,
    Key,
    MouseEvent,
    ReactNode,
    SyntheticEvent,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import './DeliveryManagement.scss';
import Grid from '@mui/material/Unstable_Grid2';
import {
    Autocomplete,
    AutocompleteRenderGetTagProps,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    FormControl,
    MenuItem,
    Select,
    SelectChangeEvent,
    Tab,
    Tabs,
    TextField,
    Typography,
    Tooltip,
    IconButton,
    SvgIcon,
    Menu,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Divider,
} from '@mui/material';
import InfoTwoToneIcon from '@mui/icons-material/InfoTwoTone';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { Header as TableHeader, ColumnDef, CoreRow, HeaderContext, Row, CellContext, Column } from '@tanstack/react-table';
import {
    DeliveryManagementFilters,
    DateFormats,
    TimeFormats,
    DeliveryManagementMapping,
    SortDirection,
    DeliveryManagementLateReasons,
    DeliveryManagementColumnFilters,
    FmtpError,
    FmtpErrorCode,
} from '@lanterds/types';
import dayjs, { Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { LocalizationProvider, DatePicker, TimeField, DatePickerSlotProps, FieldRef, FieldSection } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {
    undoDeliveryRecord,
    getDeliveryData,
    getFilters,
    getLateReasons,
    saveLateArrivals,
    saveMissingStops,
} from 'src/api/deliveryManagement';
import { Dataset } from './types';
import { InfiniteData } from '@tanstack/react-query';
import { useSnackbar, SnackbarKey, VariantType } from 'notistack';
import { debounce } from 'lodash';
import LoadingIndicator from 'src/components/common/LoadingIndicator/LoadingIndicator';
import ClearIcon from '@mui/icons-material/Clear';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

type TableDataMapping = Omit<
    DeliveryManagementMapping,
    'arrivalTimeLocal' | 'enteredArrivalTime' | 'modifiedDeliveryDate' | 'modifiedDeliveryTime' | 'routeDate'
> & {
    arrivalTimeLocalOriginal: string | null;
    arrivalDateLocalOriginal: string | null;
    enteredArrivalTimeOriginal: Dayjs | null;
    enteredArrivalDateOriginal: Dayjs | null;
    lateReasonOriginal: string | null;
    noDeliveryReasonOriginal: string | null;
    noFreightOriginal: boolean;
    arrivalTimeLocal: string | null;
    arrivalDateLocal: string | null;
    enteredArrivalTime: Dayjs | null;
    enteredArrivalDate: Dayjs | null;
    routeDate: Dayjs | null;
};

type DeliveryManagementFormFilters = {
    originDockDistrictName: string[];
    originDockName: string[];
    routeName: string[];
    customerName: string[];
    customerLocationName: string[];
    routeReportingRegionName: string[];
};

interface FieldErrors {
    startDateError?: string;
    endDateError?: string;
    dateDropdownError?: string;
}

interface CanFetch {
    deliveryData: boolean;
    filters: boolean;
    lateReasons: boolean;
}
interface CellErrors {
    count: number;
    rows: {
        [rowIndex: number]: {
            [key: string]: string;
        };
    };
}

interface CellUpdates {
    count: number;
    rows: {
        [key: string]: {
            [key: string]: Dayjs | string | boolean | null | undefined;
        };
    };
    errors: CellErrors;
}

enum ERROR_MESSAGES {
    START_DATE_MISSING = 'Please select a Start Date',
    END_DATE_MISSING = 'Please select an End Date',
    START_DATE_INVALID = 'Please select a valid Start Date',
    END_DATE_INVALID = 'Please select a valid End Date',
    INVALID_FUTURE_END_DATE = 'End Date cannot be today or a future date.',
    START_DATE_GREATER_THAN_END_DATE = 'Start Date cannot be after the End Date',
}

const initialCellUpdates: CellUpdates = {
    count: 0,
    rows: {},
    errors: {
        count: 0,
        rows: {},
    },
};

const initialFetchedFormFilters: DeliveryManagementFormFilters = {
    customerName: [],
    customerLocationName: [],
    originDockDistrictName: [],
    originDockName: [],
    routeName: [],
    routeReportingRegionName: [],
};

const initialSelectedFormFilters: Partial<DeliveryManagementFilters> = {
    startDate: dayjs().subtract(1, 'week').startOf('day').toISOString(),
    endDate: dayjs().subtract(1, 'day').startOf('day').toISOString(),
    showModified: false,
};

const initialColumnFilters: Partial<DeliveryManagementFilters> = {
    sortOrder: {
        column: 'routeDate',
        order: SortDirection.ASCENDING,
    },
};

const initialCanFetchStates: CanFetch = {
    deliveryData: true,
    filters: true,
    lateReasons: true,
};

const initialTableSort = [
    {
        id: initialColumnFilters.sortOrder?.column ?? '',
        desc: initialColumnFilters.sortOrder?.order === SortDirection.DESCENDING,
    },
];

const commonDatePickerSlotProps: DatePickerSlotProps<Dayjs, false> = {
    field: { clearable: true },
    actionBar: {
        actions: ['clear', 'today'],
        className: 'date-picker-action-bar',
    },
    textField: {
        variant: 'outlined',
        placeholder: 'MM/DD/YYYY',
        InputProps: {
            fullWidth: true,
        },
    },
};

const CHECKBOX_OUTLINE_ICON = <CheckBoxOutlineBlankIcon fontSize="small" />;
const CHECKBOX_ICON = <CheckBoxIcon fontSize="small" />;
const DEBOUNCE_DELAY_MILLISECONDS = 500;
const PAGE_SIZE = 100;
const GREATEST_VALID_END_DATE = initialSelectedFormFilters.endDate;

export default function DeliveryManagement() {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const [fetchedFormFilters, setFetchedFilters] = useState<DeliveryManagementFormFilters>(initialFetchedFormFilters);
    const [selectedFormFilters, setSelectedFormFilters] =
        useState<Partial<DeliveryManagementFilters>>(initialSelectedFormFilters);
    const [selectedColumnFilters, setSelectedColumnFilters] =
        useState<Partial<DeliveryManagementFilters>>(initialColumnFilters);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
    const [currentDataset, setCurrentDataset] = useState(Dataset.MissingStops);
    const [canFetchData, setCanFetchData] = useState<CanFetch>(initialCanFetchStates);
    const [cellUpdates, setCellUpdates] = useState<CellUpdates>(initialCellUpdates);
    const [lateReasons, setLateReasons] = useState<DeliveryManagementLateReasons[]>([]);
    const [noDeliveryReasons, setNoDeliveryReasons] = useState<DeliveryManagementLateReasons[]>([]);
    const [fetchedAllData, setFetchedAllData] = useState(false);
    const [fetchedDeliveryData, setFetchedDeliveryData] = useState<InfiniteData<
        { data: TableDataMapping[]; totalRecords: number },
        unknown
    > | null>(null);
    const [clearFilters, setClearFilters] = useState(false);
    const [contextMenu, setContextMenu] = useState<{
        userId: string;
        keySource: string;
        keyValue: string;
        mouseX: number;
        mouseY: number;
    } | null>(null);
    const [undoDialogOpen, setUndoDialogOpen] = useState(false);
    const [pendingUndo, setPendingUndo] = useState<{
        keySource: string;
        keyValue: string;
        userId: string;
    } | null>(null);

    const fetchMoreDataSnackbarId = useRef<SnackbarKey | undefined>(undefined);
    const undoMenuItem = useRef<HTMLLIElement | null>(null);
    const fetchSnackbarRef = useRef<SnackbarKey | null>(null);

    const {
        data: fetchedDeliveryResults,
        fetchNextPage,
        hasNextPage,
        // isFetching is true for all queries
        isFetching: isDeliveriesFetching,
        // isLoading is true only on init query
        isLoading: isDeliveriesLoading,
        refetch: refetchDeliveriesData,
        isFetchingNextPage: isFetchingNextDeliveriesPage,
        isRefetching: isDeliveriesRefetching,
        isPlaceholderData,
    } = getDeliveryData(
        currentDataset,
        { ...selectedFormFilters, ...selectedColumnFilters, pageSize: PAGE_SIZE } as DeliveryManagementFilters,
        canFetchData.deliveryData,
    );

    const { data: fetchedLateReasons, isFetching: isLateReasonsFetching } = getLateReasons(canFetchData.lateReasons);

    const {
        mutateAsync: updateMissingStops,
        isPending: isUpdateMissingStopsPending,
        isSuccess: isupdateMissingStopsSuccess,
        reset: resetMissingStopsMutateQuery,
        data: updateMissingStopsResult,
    } = saveMissingStops();

    const {
        mutateAsync: updateLateArrivals,
        isPending: isUpdateLateArrivalsPending,
        isSuccess: isupdateLateArrivalsSuccess,
        reset: resetLateArrivalsMutateQuery,
        data: updateLateArrivalsResult,
    } = saveLateArrivals();

    const {
        mutateAsync: undoRecord,
        isPending: isUndoingRecordPending,
        isSuccess: isUndoingRecordSuccess,
        reset: resetUndoRecordMutateQuery,
        data: undoRecordResult,
    } = undoDeliveryRecord();

    const { data: fetchedFilters, isFetching: isFiltersFetching } = getFilters(canFetchData.filters);

    useEffect(() => {
        if (fetchedDeliveryResults && !isDeliveriesLoading && !isDeliveriesFetching && !isFetchingNextDeliveriesPage) {
            if (canFetchData.deliveryData) {
                setCanFetchData({ ...canFetchData, deliveryData: false });
            }

            if (!hasNextPage && !fetchedAllData && !isPlaceholderData) {
                enqueueSnackbar('All data has been fetched.', {
                    variant: 'info',
                    preventDuplicate: true,
                    action: (key) => (
                        <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                            Dismiss
                        </Button>
                    ),
                });
                setFetchedAllData(true);
            }

            setFetchedDeliveryData(fetchedDeliveryResults);
        }
    }, [
        hasNextPage,
        fetchedAllData,
        enqueueSnackbar,
        closeSnackbar,
        fetchedDeliveryResults,
        isDeliveriesFetching,
        isDeliveriesLoading,
        isFetchingNextDeliveriesPage,
        canFetchData.deliveryData,
        isPlaceholderData,
    ]);

    const getLateReasonValue = useCallback(
        (lateReason: string) => {
            switch (currentDataset) {
                case Dataset.MissingStops:
                    return (
                        noDeliveryReasons.find((reason) => reason.lateReasonReportingValue === lateReason)
                            ?.lateReasonReportingName ?? ''
                    );
                case Dataset.LateArrivals:
                    return (
                        lateReasons.find((reason) => reason.lateReasonReportingValue === lateReason)?.lateReasonReportingName ??
                        ''
                    );
                default: {
                    const message = 'Selected Dataset is not valid.';
                    const errorCode: FmtpErrorCode = 'SHU013';
                    console.error(message, errorCode);
                    throw new FmtpError({
                        message,
                        errorCode,
                    });
                }
            }
        },
        [currentDataset, noDeliveryReasons, lateReasons],
    );

    const flatTableData: Partial<TableDataMapping>[] | null = useMemo(() => {
        if (fetchedDeliveryData?.pages.length) {
            const getEnteredArrivalTime = (enteredArrivalTime: string | Dayjs | null) => {
                if (enteredArrivalTime) {
                    return dayjs(
                        dayjs()
                            .hour(dayjs(enteredArrivalTime).utc().hour())
                            .minute(dayjs(enteredArrivalTime).utc().minute())
                            .second(0)
                            .millisecond(0),
                    );
                }
                return null;
            };

            return fetchedDeliveryData.pages
                .flatMap((page) => page.data)
                .map((item, index) => {
                    return {
                        ...item,
                        // Keeping track of original values so when users cancel or undo changes, we can compare
                        // and enable/disable the save button
                        enteredArrivalTimeOriginal: getEnteredArrivalTime(item.enteredArrivalTime),
                        arrivalTimeLocalOriginal: item.calculatedArrival
                            ? dayjs.utc(item.calculatedArrival).format(TimeFormats.TwelveHour)
                            : null,
                        arrivalDateLocalOriginal: item.calculatedArrival
                            ? dayjs.utc(item.calculatedArrival).format(DateFormats.IsoDate)
                            : null,
                        enteredArrivalDateOriginal: item.enteredArrivalTime
                            ? dayjs(dayjs(item.enteredArrivalTime).utc().format(DateFormats.ShortDate)).startOf('date')
                            : null,
                        lateReasonOriginal: item.lateReason ?? '',
                        noDeliveryReasonOriginal: item.noDeliveryReason,
                        noFreightOriginal: item.noFreight ?? false,
                        enteredArrivalDate: item.enteredArrivalTime
                            ? dayjs(dayjs(item.enteredArrivalTime).utc().format(DateFormats.ShortDate)).startOf('date')
                            : null,
                        enteredArrivalTime: getEnteredArrivalTime(item.enteredArrivalTime),
                        routeDate: item.routeDate ? dayjs(dayjs(item.routeDate).utc().format(DateFormats.ShortDate)) : null,
                        arrivalDateLocal: item.calculatedArrival
                            ? dayjs.utc(item.calculatedArrival).format(DateFormats.ShortDateNoYear)
                            : '',
                        lateReason:
                            item.lateReason && item.lateReason !== 'No Freight|NO' ? getLateReasonValue(item.lateReason) : '',
                        arrivalTimeLocal: item.calculatedArrival
                            ? dayjs.utc(item.calculatedArrival).format(TimeFormats.TwelveHour)
                            : '',
                        ...(cellUpdates.rows[index] ? cellUpdates.rows[index] : {}),
                    };
                });
        } else {
            return null;
        }
    }, [fetchedDeliveryData, cellUpdates, getLateReasonValue]);

    useEffect(() => {
        const resetSuccessStates = () => {
            setFetchedDeliveryData(null);
            setCellUpdates(initialCellUpdates);
            setFetchedAllData(false);
        };

        const getPlural = (count: number) => (count > 1 ? 's' : '');

        if (isupdateMissingStopsSuccess || isupdateLateArrivalsSuccess || isUndoingRecordSuccess) {
            let message = '';
            let variant: VariantType = 'success';

            if (isupdateMissingStopsSuccess) {
                const { updatedRecordsCount, error } = updateMissingStopsResult;

                if (updatedRecordsCount) {
                    const s = getPlural(updatedRecordsCount);
                    message = `${updatedRecordsCount} Missing Arrival${s} Successfully Updated!`;
                    resetSuccessStates();
                    resetMissingStopsMutateQuery();
                    refetchDeliveriesData();
                } else {
                    variant = 'error';
                    if (error && error.name === 'FmtpError' && error.memo) {
                        message = error.memo;
                    } else {
                        message = 'Failed to update Missing Arrival records.';
                    }
                }
            }
            if (isupdateLateArrivalsSuccess) {
                const { updatedRecordsCount } = updateLateArrivalsResult;
                if (updatedRecordsCount) {
                    const s = getPlural(updatedRecordsCount);
                    message = `${updatedRecordsCount} Late Stop${s} Successfully Updated!`;
                    resetSuccessStates();
                    resetLateArrivalsMutateQuery();
                    refetchDeliveriesData();
                } else {
                    variant = 'error';
                    message = 'Failed to update Late Stop record.';
                }
            }
            if (isUndoingRecordSuccess) {
                const { updatedRecordsCount } = undoRecordResult;

                if (updatedRecordsCount) {
                    const s = getPlural(updatedRecordsCount);
                    message = `${updatedRecordsCount} Record${s} Successfully Undone!`;
                    resetSuccessStates();
                    resetUndoRecordMutateQuery();
                    refetchDeliveriesData();
                } else {
                    variant = 'error';
                    message = 'Failed to undo record.';
                }
            }
            enqueueSnackbar(message, {
                variant: variant,
                preventDuplicate: true,
                disableWindowBlurListener: true,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
        }
    }, [
        isupdateMissingStopsSuccess,
        isupdateLateArrivalsSuccess,
        isUndoingRecordSuccess,
        undoRecordResult,
        updateLateArrivalsResult,
        updateLateArrivalsResult,
        enqueueSnackbar,
        closeSnackbar,
        resetMissingStopsMutateQuery,
        resetLateArrivalsMutateQuery,
        resetUndoRecordMutateQuery,
        refetchDeliveriesData,
    ]);

    useEffect(() => {
        if (fetchedFilters && !isFiltersFetching && canFetchData.filters) {
            setFetchedFilters((prev) => ({
                ...prev,
                customerName: fetchedFilters.customerName,
                customerLocationName: fetchedFilters.customerLocationName,
                originDockDistrictName: fetchedFilters.originDockDistrictName,
                originDockName: fetchedFilters.originDockName,
                routeName: fetchedFilters.routeName,
                routeReportingRegionName: fetchedFilters.routeReportingRegionName,
            }));
            setCanFetchData({ ...canFetchData, filters: false });
        }
    }, [fetchedFilters, isFiltersFetching, canFetchData]);

    useEffect(() => {
        if (fetchedLateReasons && !isLateReasonsFetching && canFetchData.lateReasons) {
            const noDeliveryReasons = fetchedLateReasons.filter(
                (reason: DeliveryManagementLateReasons) => reason.lateReasonType === 'NO DELIVERY',
            );
            setNoDeliveryReasons(noDeliveryReasons);
            const lateReasons = fetchedLateReasons.filter(
                (reason: DeliveryManagementLateReasons, index: number) =>
                    index !== 0 && reason.lateReasonType !== 'NO DELIVERY' && reason.lateReasonReportingName !== 'NO FREIGHT',
            );
            setLateReasons(lateReasons);
            setCanFetchData({ ...canFetchData, lateReasons: false });
        }
    }, [fetchedLateReasons, isLateReasonsFetching, canFetchData]);

    // Need to reset state after passing to fmtpTable and setting filter states
    useEffect(() => {
        if (clearFilters) {
            setClearFilters(false);
        }
    }, [clearFilters]);

    const isFetchingNextPage = () => {
        let isFetching = false;

        if (
            isFetchingNextDeliveriesPage ||
            (fetchedDeliveryResults &&
                fetchedDeliveryData &&
                fetchedDeliveryResults.pages.length > fetchedDeliveryData.pages.length &&
                flatTableData &&
                // Check if flatTableData has been updated based on fetchedDeliveryData total items
                (fetchedDeliveryData.pages.length - 1) * PAGE_SIZE +
                    fetchedDeliveryData.pages[fetchedDeliveryData.pages.length - 1].data.length ===
                    flatTableData.length)
        ) {
            isFetching = true;
        }

        return isFetching;
    };

    const fetchMoreOnBottomReached = useCallback(
        (containerRefElement?: HTMLDivElement | null) => {
            if (containerRefElement) {
                if (!fetchedAllData) {
                    // Once the user has scrolled within 60% of the bottom of the table, fetch more data if there is any
                    const { scrollHeight, scrollTop } = containerRefElement;
                    const endOfEndpointData = scrollTop / scrollHeight >= 0.6;
                    const canFetchNextPage = !!(endOfEndpointData && hasNextPage && !isFetchingNextPage());

                    if (endOfEndpointData && canFetchNextPage) {
                        fetchNextPage();
                    }
                }
            }
        },
        [
            fetchedAllData,
            hasNextPage,
            isPlaceholderData,
            isFetchingNextDeliveriesPage,
            fetchedDeliveryResults,
            fetchedDeliveryData,
            flatTableData,
            fetchNextPage,
        ],
    );

    useEffect(() => {
        if (fetchMoreDataSnackbarId.current && !isFetchingNextPage()) {
            closeSnackbar(fetchMoreDataSnackbarId.current);
            fetchMoreDataSnackbarId.current = undefined;
        }
    }, [fetchMoreDataSnackbarId, isFetchingNextPage, closeSnackbar]);

    const isFetchingInitData = () => {
        const fetching = !!(
            !isFetchingNextPage() &&
            (isDeliveriesLoading ||
                isDeliveriesRefetching ||
                isUpdateMissingStopsPending ||
                isUpdateLateArrivalsPending ||
                isUndoingRecordPending ||
                (fetchedDeliveryResults && !fetchedDeliveryData) ||
                (fetchedDeliveryData && !flatTableData) ||
                // Check if fetchedDeliveryData has been updated based on fetchedDeliveryResults total items
                (fetchedDeliveryResults &&
                    fetchedDeliveryData &&
                    fetchedDeliveryResults.pages.length !== fetchedDeliveryData.pages.length) ||
                // Check if flatTableData has been updated based on fetchedDeliveryData total items
                (fetchedDeliveryData &&
                    flatTableData &&
                    (fetchedDeliveryData.pages.length - 1) * PAGE_SIZE +
                        fetchedDeliveryData.pages[fetchedDeliveryData.pages.length - 1].data.length !==
                        flatTableData.length))
        );
        return fetching;
    };

    useEffect(() => {
        let message: string | null = null;

        if (isFetchingNextDeliveriesPage) {
            message = 'Fetching more delivery data…';
        } else if (isDeliveriesLoading) {
            message = 'Loading delivery data…';
        } else if (isDeliveriesRefetching) {
            message = 'Refreshing delivery data…';
        }

        if (message) {
            if (fetchSnackbarRef.current) {
                closeSnackbar(fetchSnackbarRef.current);
            }
            fetchSnackbarRef.current = enqueueSnackbar(message, {
                variant: 'loading',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                autoHideDuration: null,
                action: (key) => (
                    <Button size="small" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
        } else if (fetchSnackbarRef.current) {
            closeSnackbar(fetchSnackbarRef.current);
            fetchSnackbarRef.current = null;
        }
    }, [isFetchingNextDeliveriesPage, isDeliveriesLoading, isDeliveriesRefetching, enqueueSnackbar, closeSnackbar]);

    const getIsError = useCallback(
        (row: Row<Partial<TableDataMapping>>, columnName: keyof TableDataMapping, value: Dayjs | null) => {
            let isError = false;

            switch (columnName) {
                case 'enteredArrivalDate':
                    if (
                        // does not have arrival date and has arrival time
                        (!value && row.getValue('enteredArrivalTime')) ||
                        // has arrival date
                        (value &&
                            // and route date is valid (should always be)
                            dayjs(row.getValue('routeDate')).isValid() &&
                            // and arrival date is not a valid date
                            (!dayjs(value).isValid() ||
                                // or arrival date is before the route date
                                dayjs(value).isBefore(dayjs(row.getValue('routeDate'))) ||
                                // or arrival date is after today
                                dayjs(value).isAfter(dayjs())))
                    ) {
                        isError = true;
                    }
                    break;
                case 'enteredArrivalTime':
                    if (
                        // does not have arrival time and has arrival date
                        (!value && row.getValue('enteredArrivalDate')) ||
                        // has arrival time
                        (value &&
                            // and arrival time is not a valid date
                            (!dayjs(value).isValid() ||
                                // or has a valid arrival date
                                (dayjs(row.getValue('enteredArrivalDate')).isValid() &&
                                    // and arrival date and time is same date as route date
                                    // and arrival time is earlier than 5am
                                    (dayjs(row.getValue('enteredArrivalDate'))
                                        .hour(value.hour())
                                        .minute(value.minute())
                                        .isSameOrBefore(dayjs(row.getValue('routeDate')).hour(4).minute(59)) ||
                                        // or has arrival date and it is greater than now
                                        dayjs(row.getValue('enteredArrivalDate'))
                                            .hour(value.hour())
                                            .minute(value.minute())
                                            // second and millisecond is zero'd out in flatTableData
                                            .isSameOrAfter(dayjs().second(0).millisecond(0).add(1, 'minute')))) ||
                                // does not have arrival date
                                (!row.getValue('enteredArrivalDate') &&
                                    // and arrival time is greater than now
                                    dayjs()
                                        .hour(value.hour())
                                        .minute(value.minute())
                                        .second(0)
                                        .millisecond(0)
                                        .isSameOrAfter(dayjs().second(0).millisecond(0).add(1, 'minute')))))
                    ) {
                        isError = true;
                    }
                    break;
                default:
                    break;
            }
            return isError;
        },
        [],
    );

    const getColumnHeaderDisplayValue = (column: Column<Partial<TableDataMapping>, unknown>) => {
        const header = column.columnDef.header;

        if (typeof header === 'function') {
            const fakeHeaderContext = {} as HeaderContext<Partial<TableDataMapping>, unknown>;
            return header(fakeHeaderContext);
        }
        return column.id; // Return column.id as fallback
    };

    const handleUpdateErrorStates = useCallback(
        (
            row: Row<Partial<TableDataMapping>>,
            column: Column<Partial<TableDataMapping>, unknown>,
            errors: CellErrors,
            isError: boolean,
        ) => {
            const { id: columnId } = column;

            if (isError) {
                const { rows, count } = errors;
                const tempErrors = { ...rows };
                let errorCount = count;

                if (tempErrors[row.index]) {
                    if (!tempErrors[row.index][columnId]) {
                        errorCount += 1;
                    }
                    tempErrors[row.index][columnId] = getColumnHeaderDisplayValue(column);
                } else {
                    tempErrors[row.index] = {
                        [columnId]: getColumnHeaderDisplayValue(column),
                    };
                    errorCount += 1;
                }

                return {
                    count: errorCount,
                    rows: tempErrors,
                };
            } else if (row.index in errors.rows && columnId in errors.rows[row.index]) {
                const { rows, count } = errors;
                const tempErrors = { ...rows };
                const errorCount = count;
                delete tempErrors[row.index][columnId];
                if (!Object.keys(tempErrors[row.index]).length) {
                    delete tempErrors[row.index];
                }
                return { count: errorCount - 1, rows: tempErrors };
            } else {
                return errors;
            }
        },
        [getColumnHeaderDisplayValue],
    );

    const handleCellErrorUpdates = useCallback(
        (
            row: Row<Partial<TableDataMapping>>,
            column: Column<Partial<TableDataMapping>, unknown>,
            value: unknown,
            errors: CellErrors,
        ) => {
            const columnId = column.id as keyof TableDataMapping;
            const isError = getIsError(row, columnId, value as Dayjs | null);
            let newErrors = handleUpdateErrorStates(row, column, errors, isError);

            switch (column.id) {
                case 'enteredArrivalDate':
                    {
                        const timeCell = row.getAllCells().find((cell) => cell.column.id === 'enteredArrivalTime');
                        if (timeCell && timeCell.column) {
                            let isTimeError = false;
                            if (value && !row.getValue('enteredArrivalTime')) {
                                isTimeError = true;
                            }
                            newErrors = handleUpdateErrorStates(row, timeCell.column, newErrors, isTimeError);
                        }
                    }
                    break;
                case 'enteredArrivalTime':
                    {
                        const dateCell = row.getAllCells().find((cell) => cell.column.id === 'enteredArrivalDate');
                        if (dateCell && dateCell.column) {
                            let isDateError = false;
                            if (value && !row.getValue('enteredArrivalDate')) {
                                isDateError = true;
                            }
                            newErrors = handleUpdateErrorStates(row, dateCell.column, newErrors, isDateError);
                        }
                    }
                    break;
                default:
                    break;
            }

            return newErrors;
        },
        [getIsError, handleUpdateErrorStates],
    );

    const handleCellDataUpdate = useCallback(
        (row: Row<Partial<TableDataMapping>>, column: Column<Partial<TableDataMapping>, unknown>, value: unknown) => {
            const errorUpdates = handleCellErrorUpdates(row, column, value, cellUpdates.errors);
            const columnId = column.id as keyof TableDataMapping;
            const newValue = value as Dayjs | string | boolean | null | undefined;
            const { rows, count } = cellUpdates;
            const tempChanges = { ...rows };
            let changeCount = count;

            if (tempChanges[row.index]) {
                if (!tempChanges[row.index][columnId]) {
                    changeCount += 1;
                }
                tempChanges[row.index][columnId] = newValue;
            } else {
                tempChanges[row.index] = {
                    [columnId]: newValue,
                };
                changeCount += 1;
            }

            const originalColumnId = `${columnId}Original` as keyof TableDataMapping;
            if (originalColumnId) {
                const dateFields: Partial<keyof TableDataMapping>[] = [
                    'arrivalTimeLocal',
                    'enteredArrivalTime',
                    'enteredArrivalDate',
                ];
                if (
                    dateFields.includes(columnId as keyof TableDataMapping) &&
                    tempChanges[row.index][columnId] &&
                    flatTableData &&
                    flatTableData[row.index][originalColumnId]
                ) {
                    if (
                        flatTableData &&
                        dayjs
                            .utc(tempChanges[row.index][columnId] as string)
                            .isSame(flatTableData[row.index][originalColumnId] as Dayjs)
                    ) {
                        delete tempChanges[row.index][columnId];
                        changeCount -= 1;
                    }
                } else if (flatTableData && tempChanges[row.index][columnId] === flatTableData[row.index][originalColumnId]) {
                    delete tempChanges[row.index][columnId];
                    changeCount -= 1;
                }
            }

            setCellUpdates({ rows: tempChanges, count: changeCount, errors: errorUpdates });
        },
        [cellUpdates, handleCellErrorUpdates],
    );

    const deliveryDateCell = useCallback(
        (props: CellContext<Partial<TableDataMapping>, unknown>) => {
            const { row, getValue: getInitialValue, table, column } = props;
            const { original } = row;
            const initialValue = getInitialValue() as Dayjs | null;
            const [value, setValue] = useState(initialValue);
            const inputRef = useRef(null);

            useEffect(() => {
                setValue(initialValue);
            }, [initialValue]);

            // For user's convenience, insert year upon user's initial change
            const insertDefaultYear = useCallback(() => {
                if (inputRef && inputRef.current) {
                    const fieldSections = (inputRef.current as FieldRef<FieldSection>).getSections();
                    const yearSection = fieldSections[2];
                    if (!yearSection.value) {
                        yearSection.value = dayjs().format('YY');
                    }
                }
            }, [inputRef.current]);

            const debounceHandleChange = debounce((newValue: Dayjs | null) => {
                const normalizedValue = newValue && newValue?.isValid() ? newValue.startOf('date') : newValue;
                setValue(normalizedValue);
                if (table.options.meta?.updateData && (normalizedValue === null || normalizedValue?.isValid())) {
                    table.options.meta?.updateData(row, column, normalizedValue);
                }
            }, DEBOUNCE_DELAY_MILLISECONDS);

            const referenceDate = () => {
                return dayjs(dayjs.utc(original.routeDate).format(DateFormats.ShortDateTime));
            };

            return (
                <Box sx={{ marginLeft: '-10px' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        {/* DatePicker is a special case where we define the label in the slotProps and not as an InputLabel */}
                        <DatePicker
                            referenceDate={referenceDate()}
                            desktopModeMediaQuery="@media (min-width: 400px)"
                            name="deliveryDate"
                            value={value}
                            onChange={debounceHandleChange}
                            format="MM/DD/YY"
                            minDate={dayjs(dayjs.utc(original.routeDate).format(DateFormats.ShortDate))}
                            maxDate={dayjs()}
                            slotProps={{
                                ...commonDatePickerSlotProps,
                                field: {
                                    clearable: true,
                                    unstableFieldRef: inputRef,
                                },
                                textField: {
                                    placeholder: 'MM/DD/YY',
                                    inputProps: {
                                        onFocus: insertDefaultYear,
                                    },
                                    className: 'delivery-date',
                                    error: getIsError(row, column.id as keyof TableDataMapping, value),
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            );
        },
        [getIsError],
    );

    const editableArrivalTimeCell = useCallback(
        (props: CellContext<Partial<TableDataMapping>, unknown>) => {
            const { row, getValue: getInitialValue, table, column } = props;
            const initialValue = getInitialValue() as Dayjs | null;
            const [value, setValue] = useState<Dayjs | null>(initialValue);

            useEffect(() => {
                setValue(initialValue);
            }, [initialValue]);

            const debounceHandleChange = debounce((newValue: Dayjs | null) => {
                setValue(newValue);
                if (table.options.meta?.updateData && (newValue === null || newValue?.isValid())) {
                    table.options.meta?.updateData(row, column, newValue);
                }
            }, DEBOUNCE_DELAY_MILLISECONDS);

            return (
                <Box sx={{ marginLeft: '-10px' }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <TimeField
                            value={value}
                            format="HH:mm"
                            className="time"
                            onChange={debounceHandleChange}
                            autoCorrect="false"
                            ampm={false}
                            helperText=""
                            clearable={true}
                            slotProps={{
                                textField: {
                                    error: getIsError(row, column.id as keyof TableDataMapping, value),
                                },
                            }}
                        />
                    </LocalizationProvider>
                </Box>
            );
        },
        [getIsError],
    );

    const noDeliveryReasonCell = useCallback(
        (props: CellContext<Partial<TableDataMapping>, unknown>, noDeliveryReasons: DeliveryManagementLateReasons[]) => {
            const { row, getValue: getInitialValue, table, column } = props;
            const initialValue = getInitialValue() as string;
            const [value, setValue] = useState<string>(initialValue);

            useEffect(() => {
                setValue(initialValue);
            }, [initialValue]);

            const debounceHandleClear = debounce(() => {
                setValue('');
                if (table.options.meta?.updateData) {
                    table.options.meta?.updateData(row, column, '');
                }
            }, DEBOUNCE_DELAY_MILLISECONDS);

            const debounceHandleChange = debounce((event: SelectChangeEvent<string>) => {
                setValue(event.target.value);
                if (table.options.meta?.updateData) {
                    table.options.meta?.updateData(row, column, event.target.value);
                }
            }, DEBOUNCE_DELAY_MILLISECONDS);

            const handleRenderValue = useCallback((value: string) => {
                if (!value) {
                    return <Typography className="no-selection">TBD</Typography>;
                }
                return value;
            }, []);

            const menuItems = useMemo(() => {
                return noDeliveryReasons.map((reason, i) => {
                    return (
                        <MenuItem disableRipple value={reason.lateReasonReportingName} key={i}>
                            {reason.lateReasonReportingName}
                        </MenuItem>
                    );
                });
            }, []);

            return (
                <Box sx={{ padding: '10px 0' }}>
                    <Select
                        variant="outlined"
                        fullWidth
                        onChange={debounceHandleChange}
                        value={value}
                        className="no-delivery-reason"
                        renderValue={handleRenderValue}
                        displayEmpty={true}
                        MenuProps={{
                            autoFocus: false,
                        }}
                        IconComponent={(props) => {
                            return value ? (
                                <IconButton onClick={debounceHandleClear} disableRipple>
                                    <SvgIcon component={ClearIcon} />
                                </IconButton>
                            ) : (
                                <ArrowDropDownIcon {...props} />
                            );
                        }}
                    >
                        {menuItems}
                    </Select>
                </Box>
            );
        },
        [],
    );

    const noFreightCell = useCallback((props: CellContext<Partial<TableDataMapping>, unknown>) => {
        const { row, getValue: getInitialValue, table, column } = props;
        const initialValue = getInitialValue() as boolean;
        const [value, setValue] = useState(initialValue);

        useEffect(() => {
            setValue(initialValue);
        }, [initialValue]);

        const handleChange = useCallback((_e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setValue(checked);
            if (table.options.meta?.updateData) {
                table.options.meta?.updateData(row, column, checked);
            }
        }, []);

        return (
            <Box>
                <FormControl>
                    <Checkbox size="small" checked={value ?? false} onChange={handleChange} disableRipple />
                </FormControl>
            </Box>
        );
    }, []);

    const createToolTip = (formattedToolTip: string, innerContent: ReactNode, key: Key) => {
        const newToolTip: ReactNode[] = [];

        // Separate by stops
        const stops = formattedToolTip.split('\n');
        stops.forEach((stop, index) => {
            // Add a divider before every stop except the first
            if (index > 0) {
                newToolTip.push(<Divider key={`divider-${index}`} sx={{ my: 1 }} />);
            }

            newToolTip.push(
                stop.split(',').map((item: string, index: number) => {
                    const separatorIndex = item.indexOf(':');
                    const label = item.substring(0, separatorIndex);
                    const value = item.substring(separatorIndex + 1);

                    return (
                        <Box className="tooltip-item-container" key={index}>
                            <Box className="tooltip-item-label">{`${label.trim()}:`}</Box>
                            <Box>{value.trim()}</Box>
                        </Box>
                    );
                }),
            );
        });

        return (
            <Tooltip
                arrow
                title={
                    // Dynamically adjust right padding due to scroll bar overlapping text
                    <Box className="tooltip-items-container" sx={{ paddingRight: `${stops.length > 1 ? '.5rem' : '.25rem'}` }}>
                        {newToolTip}
                    </Box>
                }
                placement="top"
                key={key}
            >
                <Box>{innerContent}</Box>
            </Tooltip>
        );
    };

    const commitCell = useCallback((props: CellContext<Partial<TableDataMapping>, unknown>, currentDataset: Dataset) => {
        const { row, getValue } = props;
        const { formattedToolTip } = row.original;

        if (currentDataset === Dataset.LateArrivals) {
            return getValue();
        }

        if (formattedToolTip) {
            return createToolTip(formattedToolTip, getValue() as string, row.index);
        }
        return getValue() as ReactNode;
    }, []);

    const routeCell = useCallback((props: CellContext<Partial<TableDataMapping>, unknown>) => {
        const { row, getValue } = props;
        const { routeTypeName } = row.original;
        return (
            <Tooltip arrow title={routeTypeName} placement="top" key={row.index}>
                <Box>{getValue() as string}</Box>
            </Tooltip>
        );
    }, []);

    const arrivalTimeCell = useCallback((props: CellContext<Partial<TableDataMapping>, unknown>) => {
        const { row, getValue } = props;
        const { formattedToolTip } = row.original;

        if (formattedToolTip) {
            return createToolTip(formattedToolTip, getValue() as string, row.index);
        }
        return getValue() as ReactNode;
    }, []);

    const lateReasonCell = useCallback(
        (props: CellContext<Partial<TableDataMapping>, unknown>, lateReasons: DeliveryManagementLateReasons[]) => {
            const { row, getValue: getInitialValue, table, column } = props;
            const initialValue = getInitialValue() as string;
            const [value, setValue] = useState(initialValue);

            useEffect(() => {
                setValue(initialValue);
            }, [initialValue]);

            const debounceHandleClear = debounce(() => {
                setValue('');
                if (table.options.meta?.updateData) {
                    table.options.meta?.updateData(row, column, '');
                }
            }, DEBOUNCE_DELAY_MILLISECONDS);

            const debounceHandleChange = debounce((event: SelectChangeEvent<string>) => {
                setValue(event.target.value);
                if (table.options.meta?.updateData) {
                    table.options.meta?.updateData(row, column, event.target.value);
                }
            }, DEBOUNCE_DELAY_MILLISECONDS);

            const handleRenderValue = useCallback((value: string) => {
                if (!value) {
                    return <Typography className="no-selection">Select Option</Typography>;
                }
                return value;
            }, []);

            const menuItems = useMemo(() => {
                return lateReasons.map((reason, i) => {
                    return (
                        <MenuItem disableRipple value={reason.lateReasonReportingName} key={i}>
                            {reason.lateReasonReportingName}
                        </MenuItem>
                    );
                });
            }, []);

            return (
                <Box sx={{ padding: '10px 0' }}>
                    <Select
                        variant="outlined"
                        fullWidth
                        onChange={debounceHandleChange}
                        value={value}
                        className="late-reason"
                        renderValue={handleRenderValue}
                        displayEmpty={true}
                        MenuProps={{
                            autoFocus: false,
                        }}
                        IconComponent={(props) => {
                            return value ? (
                                <IconButton onClick={debounceHandleClear} disableRipple>
                                    <SvgIcon component={ClearIcon} />
                                </IconButton>
                            ) : (
                                <ArrowDropDownIcon {...props} />
                            );
                        }}
                    >
                        {menuItems}
                    </Select>
                </Box>
            );
        },
        [],
    );

    const columns = useMemo(() => {
        let columnDefinitions: ColumnDef<Partial<TableDataMapping>>[] = [
            {
                accessorKey: 'customerLocationName',
                header: 'PDC',
            },
            {
                accessorKey: 'routeReportingRegionName',
                header: 'Region',
            },
            {
                accessorKey: 'originDockName',
                header: 'Terminal',
                size: 200,
            },
            {
                accessorKey: 'routeName',
                header: 'Route',
                size: 210,
                meta: {
                    innerHtml: routeCell,
                },
            },
            {
                accessorKey: 'lNumber',
                header: 'L Number',
                size: 150,
            },
            {
                accessorKey: 'dealerName',
                header: 'Dealer Name',
                size: 300,
            },
            {
                accessorKey: 'address',
                header: 'Address',
                size: 200,
            },
            {
                accessorKey: 'city',
                header: 'City',
            },
            {
                accessorKey: 'state',
                header: 'ST',
                size: 150,
            },
            {
                accessorKey: 'routeDate',
                header: 'Route Date',
                size: 150,
                meta: {
                    innerHtml: (props) => {
                        const { getValue } = props;
                        return (getValue() as Dayjs).format(DateFormats.ShortYearDate);
                    },
                },
            },
        ];

        switch (currentDataset) {
            case Dataset.MissingStops:
                {
                    const missingStopsColumns: ColumnDef<Partial<TableDataMapping>>[] = [
                        {
                            accessorKey: 'commitTime',
                            header: 'Commit',
                            size: 150,
                            meta: {
                                innerHtml: (props) => commitCell(props, currentDataset),
                            },
                        },
                        {
                            accessorKey: 'enteredArrivalDate',
                            header: 'Delivery Date',
                            size: 200,
                            meta: {
                                innerHtml: deliveryDateCell,
                            },
                        },
                        {
                            accessorKey: 'enteredArrivalTime',
                            header: 'Time',
                            size: 150,
                            meta: {
                                innerHtml: editableArrivalTimeCell,
                            },
                        },
                        {
                            accessorKey: 'lateReason',
                            header: 'No Delivery Reason',
                            size: 200,
                            meta: {
                                innerHtml: (props) => noDeliveryReasonCell(props, noDeliveryReasons),
                            },
                        },
                        {
                            accessorKey: 'noFreight',
                            header: 'No Freight',
                            size: 120,
                            enableColumnFilter: false,
                            meta: {
                                innerHtml: noFreightCell,
                            },
                        },
                    ];
                    columnDefinitions = [...columnDefinitions, ...missingStopsColumns];
                }
                break;
            case Dataset.LateArrivals:
                {
                    const lateArrivalsColumns: ColumnDef<Partial<TableDataMapping>>[] = [
                        {
                            accessorKey: 'commitTime',
                            header: 'Commit',
                            size: 150,
                            meta: {
                                innerHtml: (props) => commitCell(props, currentDataset),
                            },
                        },
                        {
                            accessorKey: 'arrivalDateLocal',
                            header: 'Arrival Date',
                            size: 150,
                        },
                        {
                            accessorKey: 'arrivalTimeLocal',
                            header: 'Arrival Time',
                            size: 150,
                            meta: {
                                innerHtml: arrivalTimeCell,
                            },
                        },
                        {
                            accessorKey: 'lateReason',
                            header: 'Late Reason',
                            size: 170,
                            meta: {
                                innerHtml: (props) => lateReasonCell(props, lateReasons),
                            },
                        },
                    ];
                    columnDefinitions = [...columnDefinitions, ...lateArrivalsColumns];
                }
                break;
            default: {
                const message = 'Selected Dataset is not valid.';
                const errorCode: FmtpErrorCode = 'SHU016';
                console.error(message, errorCode);
                throw new FmtpError({
                    message,
                    errorCode,
                });
            }
        }

        if (selectedFormFilters.showModified) {
            const showModifiedColumns: ColumnDef<Partial<TableDataMapping>>[] = [
                {
                    accessorKey: 'updatedUser',
                    header: 'Update User',
                    size: 200,
                    meta: {
                        headerHtml: (_props, displayText) => {
                            return (
                                <div>
                                    {displayText}
                                    <Tooltip arrow title={'Right click a user to undo entry.'} placement="top">
                                        <InfoTwoToneIcon />
                                    </Tooltip>
                                </div>
                            );
                        },
                    },
                },
                {
                    accessorKey: 'updatedDate',
                    header: 'Update Date',
                    size: 200,
                },
            ];
            columnDefinitions = [...columnDefinitions, ...showModifiedColumns];
        }

        return columnDefinitions;
    }, [currentDataset, noDeliveryReasons, lateReasons]);

    const handleFormFilterChange = useCallback(
        (value: string[] | null, filterName: keyof DeliveryManagementFormFilters) => {
            const tempFilters = { ...selectedFormFilters };
            if (!value?.length && filterName in tempFilters) {
                delete tempFilters[filterName];
                setSelectedFormFilters({ ...tempFilters });
            } else {
                setSelectedFormFilters({ ...selectedFormFilters, [filterName]: value ? value?.join(' , ') : null });
            }
        },
        [selectedFormFilters],
    );

    const handleDateRangeChange = useCallback(
        (value: Dayjs | null, name: keyof DeliveryManagementFilters) => {
            setSelectedFormFilters({ ...selectedFormFilters, [name]: value?.format(DateFormats.IsoDate) });
        },
        [selectedFormFilters],
    );

    const handleResetForm = () => {
        setSelectedFormFilters(initialSelectedFormFilters);
    };

    const isFormValid = useMemo(() => {
        let isValid = true;
        const newFieldErrors: FieldErrors = {};
        let errorCount = 0;
        const startDate = selectedFormFilters.startDate;
        const endDate = selectedFormFilters.endDate;

        if (!startDate) {
            newFieldErrors.startDateError = ERROR_MESSAGES.START_DATE_MISSING;
            errorCount += 1;
        }

        if (!endDate) {
            newFieldErrors.endDateError = ERROR_MESSAGES.END_DATE_MISSING;
            errorCount += 1;
        }

        if (!dayjs(selectedFormFilters.startDate).isValid()) {
            newFieldErrors.startDateError = ERROR_MESSAGES.START_DATE_INVALID;
            errorCount += 1;
        }

        if (!dayjs(selectedFormFilters.endDate).isValid()) {
            newFieldErrors.endDateError = ERROR_MESSAGES.END_DATE_INVALID;
            errorCount += 1;
        }

        if (
            endDate &&
            dayjs(selectedFormFilters.endDate).isValid() &&
            dayjs(selectedFormFilters.endDate).isAfter(dayjs(GREATEST_VALID_END_DATE))
        ) {
            newFieldErrors.endDateError = ERROR_MESSAGES.INVALID_FUTURE_END_DATE;
            errorCount += 1;
        }

        if (dayjs(selectedFormFilters.startDate)?.isValid() && dayjs(selectedFormFilters.endDate)?.isValid()) {
            if (dayjs(selectedFormFilters.endDate).isBefore(dayjs(selectedFormFilters.startDate))) {
                newFieldErrors.startDateError = ERROR_MESSAGES.START_DATE_GREATER_THAN_END_DATE;
                errorCount += 1;
            }
        }

        if (errorCount >= 1) {
            setFieldErrors(newFieldErrors);
            isValid = false;
        }

        if (isValid) {
            setFieldErrors({});
        }

        return isValid;
    }, [selectedFormFilters]);

    const applyFiltersAndSearch = useCallback(() => {
        if (isFormValid) {
            resetCommonStates();
            setSelectedColumnFilters(initialColumnFilters);
            setClearFilters(true);
        } else {
            enqueueSnackbar('Please correct form errors before searching.', {
                variant: 'error',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
        }
    }, [isFormValid, enqueueSnackbar, closeSnackbar]);

    const renderTags = useCallback((value: string[], getTagProps: AutocompleteRenderGetTagProps) => {
        const numTags = value.length;
        const limitTags = 1;
        return (
            <>
                {value.slice(0, limitTags).map((option: string, index: number) => (
                    <Chip {...getTagProps({ index })} key={index} label={option} />
                ))}

                {numTags > limitTags && ` +${numTags - limitTags}`}
            </>
        );
    }, []);

    const handleTabChange = useCallback(
        (_event: SyntheticEvent, newValue: number) => {
            if (isFormValid) {
                setCurrentDataset(newValue === 0 ? Dataset.MissingStops : Dataset.LateArrivals);
                setClearFilters(true);
                resetCommonStates();
                setSelectedColumnFilters(initialColumnFilters);
            } else {
                const errors = Object.values(fieldErrors);
                enqueueSnackbar(`${errors[0]}`, {
                    variant: 'error',
                    preventDuplicate: true,
                    disableWindowBlurListener: true,
                    action: (key) => (
                        <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                            Dismiss
                        </Button>
                    ),
                });
            }
        },
        [enqueueSnackbar, closeSnackbar, isFormValid],
    );

    const handleShowModifiedChange = useCallback(
        (_event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setSelectedFormFilters({ ...selectedFormFilters, showModified: checked });
        },
        [selectedFormFilters],
    );

    const handleCancelChanges = () => {
        setCellUpdates(initialCellUpdates);
    };

    const saveSnackbarRef = useRef<SnackbarKey | null>(null);

    const handleSaveChanges = useCallback(async () => {
        saveSnackbarRef.current = enqueueSnackbar('Saving changes…', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });
        if (cellUpdates.errors.count) {
            const errors = Object.values(cellUpdates.errors.rows);
            const headerNames = Object.values(errors[0]);
            let message = 'Error while attempting to save changes.';
            if (cellUpdates.errors.count) message = `The ${headerNames[0]} column contains an invalid value.`;

            enqueueSnackbar(message, {
                variant: 'error',
                preventDuplicate: true,
                disableWindowBlurListener: true,
                action: (key) => (
                    <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                        Dismiss
                    </Button>
                ),
            });
            return;
        }
        const changedData = { ...cellUpdates };
        const rowNumbers = Object.keys(changedData.rows);
        const updatesToSave: Partial<DeliveryManagementMapping>[] = [];

        try {
            switch (currentDataset) {
                case Dataset.MissingStops:
                    if (flatTableData) {
                        rowNumbers.forEach((rowNumber) => {
                            const {
                                keySource,
                                keyValue,
                                stopDate,
                                noFreight,
                                enteredArrivalTime,
                                enteredArrivalDate,
                                lateReason,
                            } = flatTableData[Number(rowNumber)];
                            const lateReasonValue = noDeliveryReasons?.find(
                                (reason: DeliveryManagementLateReasons) => reason.lateReasonReportingName === lateReason,
                            ) as DeliveryManagementLateReasons | undefined;
                            updatesToSave.push({
                                keySource,
                                keyValue,
                                stopDate,
                                noDeliveryReason: lateReasonValue ? lateReasonValue.lateReasonReportingValue : null,
                                noFreight,
                                modifiedDeliveryDate: enteredArrivalDate
                                    ? dayjs(enteredArrivalDate).format(DateFormats.IsoDate)
                                    : null,
                                modifiedDeliveryTime: enteredArrivalTime
                                    ? `${dayjs(enteredArrivalTime).format(TimeFormats.TwentyFourHour)}:00.000`
                                    : null,
                            });
                        });

                        await updateMissingStops(updatesToSave);
                    }

                    break;

                case Dataset.LateArrivals:
                    if (flatTableData) {
                        rowNumbers.forEach((rowNumber) => {
                            const { keySource, keyValue, lateReason } = flatTableData[Number(rowNumber)];

                            const lateReasonValue = lateReasons?.find(
                                (reason: DeliveryManagementLateReasons) => reason.lateReasonReportingName === lateReason,
                            ) as DeliveryManagementLateReasons | undefined;

                            updatesToSave.push({
                                keySource,
                                keyValue,
                                lateReason: lateReasonValue ? lateReasonValue.lateReasonReportingValue : null,
                            });
                        });

                        await updateLateArrivals(updatesToSave);
                    }

                    break;
                default: {
                    const message = 'Selected Dataset is not valid.';
                    const errorCode: FmtpErrorCode = 'SHU015';
                    console.error(message, errorCode);
                    throw new FmtpError({
                        message,
                        errorCode,
                    });
                }
            }
            enqueueSnackbar('Changes saved!', { variant: 'success', preventDuplicate: true });
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to save changes.', { variant: 'error', preventDuplicate: true });
        } finally {
            closeSnackbar(saveSnackbarRef.current);
            saveSnackbarRef.current = null;
        }
    }, [
        flatTableData,
        cellUpdates,
        currentDataset,
        lateReasons,
        noDeliveryReasons,
        updateLateArrivals,
        updateMissingStops,
        enqueueSnackbar,
        closeSnackbar,
    ]);

    useEffect(() => {
        return () => {
            if (fetchMoreDataSnackbarId.current != null) {
                closeSnackbar(fetchMoreDataSnackbarId.current);
                fetchMoreDataSnackbarId.current = undefined;
            }
            if (fetchSnackbarRef.current != null) {
                closeSnackbar(fetchSnackbarRef.current);
                fetchSnackbarRef.current = null;
            }
            if (saveSnackbarRef.current != null) {
                closeSnackbar(saveSnackbarRef.current);
                saveSnackbarRef.current = null;
            }
        };
    }, [closeSnackbar]);

    const resetCommonStates = () => {
        setFetchedDeliveryData(null);
        setCellUpdates(initialCellUpdates);
        setFetchedAllData(false);
        setCanFetchData({ ...canFetchData, deliveryData: true });
    };

    const handleSortChange = useCallback(
        (header: TableHeader<Partial<TableDataMapping>, unknown>) => {
            const { sortOrder } = selectedColumnFilters ?? {};
            const { column, order } = sortOrder ?? {};
            const { id: headerId } = header;

            // Need to handle special case where 'arrivalDateLocal' is only a local column based on 'arrivalTimeLocal' value
            // so we will pass 'arrivalTimeLocal' to the BE
            const headerName =
                headerId === 'arrivalDateLocal' ? 'arrivalTimeLocal' : (headerId as keyof DeliveryManagementMapping);

            if (headerId) {
                if (headerId === column) {
                    const newOrder = order === SortDirection.ASCENDING ? SortDirection.DESCENDING : SortDirection.ASCENDING;

                    setSelectedColumnFilters({
                        ...selectedColumnFilters,
                        sortOrder: {
                            column: headerName,
                            order: newOrder,
                        },
                    });
                    header.column.toggleSorting(newOrder === SortDirection.DESCENDING);
                } else {
                    setSelectedColumnFilters({
                        ...selectedColumnFilters,
                        sortOrder: {
                            column: headerName,
                            order: SortDirection.DESCENDING,
                        },
                    });
                    header.column.toggleSorting(true);
                }
                resetCommonStates();
            }
        },
        [selectedColumnFilters.sortOrder, resetCommonStates],
    );

    const handleColumnFilterChange = useCallback(
        (name: string, value: string | number | boolean) => {
            // Need to handle special case where 'arrivalDateLocal' is only a local column based on 'arrivalTimeLocal' value
            // so we will pass 'arrivalTimeLocal' to the BE
            const headerName = name === 'arrivalDateLocal' ? 'arrivalTimeLocal' : name;

            if (selectedColumnFilters.columnFilters || value) {
                setSelectedColumnFilters({
                    ...selectedColumnFilters,
                    columnFilters: {
                        ...selectedColumnFilters.columnFilters,
                        [headerName]: value,
                    } as DeliveryManagementColumnFilters,
                });
                resetCommonStates();
            }
        },
        [selectedColumnFilters.columnFilters, resetCommonStates],
    );

    const loadingMessage = useCallback(() => {
        const message = 'Loading';
        const gettingDeliveries = `Getting ${
            currentDataset === Dataset.MissingStops ? 'Missing Arrival Times' : 'Late Stops'
        } Data...`;
        const updatingLateStops = 'Updating Late Stops...';
        const updatingMissingArrivalTimes = 'Updating Missing Arrival Times...';
        const undoingRecord = 'Undoing Record...';

        if (isUpdateMissingStopsPending) {
            return updatingMissingArrivalTimes;
        } else if (isUpdateLateArrivalsPending) {
            return updatingLateStops;
        } else if (isUndoingRecordPending) {
            return undoingRecord;
        } else if (isDeliveriesFetching) {
            return gettingDeliveries;
        }
        return message;
    }, [
        currentDataset,
        isUpdateMissingStopsPending,
        isUpdateLateArrivalsPending,
        isUndoingRecordPending,
        isDeliveriesFetching,
    ]);

    const renderFormFilterOptions = useCallback(
        (props: HTMLAttributes<HTMLLIElement>, option: string, { selected }: { selected: boolean }) => {
            return (
                <li {...props}>
                    <Checkbox icon={CHECKBOX_OUTLINE_ICON} checkedIcon={CHECKBOX_ICON} checked={selected} />
                    {option}
                </li>
            );
        },
        [],
    );

    const handleShowUndoDialog = useCallback(() => {
        if (contextMenu) {
            setPendingUndo({
                keySource: contextMenu.keySource,
                keyValue: contextMenu.keyValue,
                userId: contextMenu.userId,
            });
            setContextMenu(null);
            setUndoDialogOpen(true);
        }
    }, [contextMenu]);

    const handleConfirmUndo = async () => {
        if (!pendingUndo) return;
        const { keySource, keyValue } = pendingUndo;
        setUndoDialogOpen(false);
        setPendingUndo(null);
        try {
            await undoRecord({ keySource, keyValue });
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to undo entry.', { variant: 'error' });
        }
    };

    const handleContextMenuBlur = useCallback(
        (event: FocusEvent) => {
            if (event.relatedTarget && undoMenuItem.current && event.relatedTarget.id === undoMenuItem.current.id) {
                handleShowUndoDialog();
            }
            setContextMenu(null);
        },
        [setContextMenu, handleShowUndoDialog],
    );

    const handleContextMenuOpen = useCallback(
        (event: MouseEvent, row: CoreRow<Partial<TableDataMapping>>) => {
            event.preventDefault();
            if (contextMenu === null) {
                const { original } = row;
                if (original.updatedUser) {
                    setContextMenu({
                        userId: original.updatedUser ?? '',
                        keySource: original.keySource ?? '',
                        keyValue: original.keyValue ?? '',
                        mouseX: event.clientX + 2,
                        mouseY: event.clientY - 6,
                    });
                }
            }
        },
        [contextMenu],
    );

    return (
        <Box id="dmt-container" className="auto-fill-height">
            <Card id="dmt-form" className="auto-fill-height">
                <Grid container spacing={2} className="form-container">
                    <Grid xs={12}>
                        <CardContent>
                            <Grid xs={12} container spacing={2}>
                                <Grid xl={2} lg={3} md={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                        <Autocomplete
                                            limitTags={1}
                                            options={fetchedFormFilters.customerName}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            loading={isFiltersFetching}
                                            renderTags={renderTags}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label="Customer"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFiltersFetching ? (
                                                                    <CircularProgress color="inherit" size={20} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    InputLabelProps={{ ...params.InputLabelProps }}
                                                />
                                            )}
                                            renderOption={renderFormFilterOptions}
                                            value={selectedFormFilters.customerName?.split(' , ') ?? []}
                                            onChange={(_event, newValue) => {
                                                handleFormFilterChange(newValue, 'customerName');
                                            }}
                                            disabled={isFiltersFetching}
                                            multiple={true}
                                            disableCloseOnSelect={true}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid xl={2} lg={3} md={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                        <Autocomplete
                                            limitTags={1}
                                            options={fetchedFormFilters.customerLocationName ?? []}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            loading={isFiltersFetching}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            renderTags={renderTags}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label="PDC"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFiltersFetching ? (
                                                                    <CircularProgress color="inherit" size={20} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    InputLabelProps={{ ...params.InputLabelProps }}
                                                />
                                            )}
                                            renderOption={renderFormFilterOptions}
                                            value={selectedFormFilters.customerLocationName?.split(' , ') ?? []}
                                            onChange={(_event, newValue) => {
                                                handleFormFilterChange(newValue, 'customerLocationName');
                                            }}
                                            disabled={isFiltersFetching}
                                            multiple={true}
                                            disableCloseOnSelect={true}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid xl={2} lg={3} md={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                        <Autocomplete
                                            limitTags={1}
                                            options={fetchedFormFilters.originDockDistrictName ?? []}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            loading={isFiltersFetching}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            renderTags={renderTags}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label="Dock District"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFiltersFetching ? (
                                                                    <CircularProgress color="inherit" size={20} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    InputLabelProps={{ ...params.InputLabelProps }}
                                                />
                                            )}
                                            renderOption={renderFormFilterOptions}
                                            value={selectedFormFilters.originDockDistrictName?.split(' , ') ?? []}
                                            onChange={(_event, newValue) => {
                                                handleFormFilterChange(newValue, 'originDockDistrictName');
                                            }}
                                            disabled={isFiltersFetching}
                                            multiple={true}
                                            disableCloseOnSelect={true}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid xl={2} lg={3} md={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                        <Autocomplete
                                            limitTags={1}
                                            options={fetchedFormFilters.originDockName ?? []}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            loading={isFiltersFetching}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            renderTags={renderTags}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label="Terminal HUB/FXD"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFiltersFetching ? (
                                                                    <CircularProgress color="inherit" size={20} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    InputLabelProps={{ ...params.InputLabelProps }}
                                                />
                                            )}
                                            renderOption={renderFormFilterOptions}
                                            value={selectedFormFilters.originDockName?.split(' , ') ?? []}
                                            onChange={(_event, newValue) => {
                                                handleFormFilterChange(newValue, 'originDockName');
                                            }}
                                            disabled={isFiltersFetching}
                                            multiple={true}
                                            disableCloseOnSelect={true}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid xl={2} lg={3} md={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                        <Autocomplete
                                            limitTags={1}
                                            options={fetchedFormFilters.routeName ?? []}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            loading={isFiltersFetching}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            renderTags={renderTags}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label="Route"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFiltersFetching ? (
                                                                    <CircularProgress color="inherit" size={20} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    InputLabelProps={{ ...params.InputLabelProps }}
                                                />
                                            )}
                                            renderOption={renderFormFilterOptions}
                                            value={selectedFormFilters.routeName?.split(' , ') ?? []}
                                            onChange={(_event, newValue) => {
                                                handleFormFilterChange(newValue, 'routeName');
                                            }}
                                            disabled={isFiltersFetching}
                                            multiple={true}
                                            disableCloseOnSelect={true}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid xl={2} lg={3} md={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                        <Autocomplete
                                            limitTags={1}
                                            options={fetchedFormFilters.routeReportingRegionName ?? []}
                                            selectOnFocus
                                            clearOnBlur
                                            handleHomeEndKeys
                                            loading={isFiltersFetching}
                                            isOptionEqualToValue={(option, value) => option === value}
                                            renderTags={renderTags}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    variant="outlined"
                                                    label="Region"
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        endAdornment: (
                                                            <>
                                                                {isFiltersFetching ? (
                                                                    <CircularProgress color="inherit" size={20} />
                                                                ) : null}
                                                                {params.InputProps.endAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                    InputLabelProps={{ ...params.InputLabelProps }}
                                                />
                                            )}
                                            renderOption={renderFormFilterOptions}
                                            value={selectedFormFilters.routeReportingRegionName?.split(' , ') ?? []}
                                            onChange={(_event, newValue) => {
                                                handleFormFilterChange(newValue, 'routeReportingRegionName');
                                            }}
                                            disabled={isFiltersFetching}
                                            multiple={true}
                                            disableCloseOnSelect={true}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid xl={4} lg={6} md={6} sm={8} xs={12} container spacing={2}>
                                    <Grid sm={6} xs={12}>
                                        <FormControl fullWidth>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                {/* DatePicker is a special case where we define the label in the slotProps and not as an InputLabel */}
                                                <DatePicker
                                                    desktopModeMediaQuery="@media (min-width: 400px)"
                                                    value={
                                                        selectedFormFilters.startDate
                                                            ? dayjs(selectedFormFilters.startDate)
                                                            : null
                                                    }
                                                    onChange={(value) => handleDateRangeChange(value, 'startDate')}
                                                    slotProps={{
                                                        ...commonDatePickerSlotProps,
                                                        textField: {
                                                            label: 'Route Start Date',
                                                            error: !!fieldErrors?.startDateError,
                                                            helperText: fieldErrors?.startDateError,
                                                        },
                                                        actionBar: {
                                                            actions: ['clear'],
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </Grid>
                                    <Grid sm={6} xs={12}>
                                        <FormControl fullWidth>
                                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                {/* DatePicker is a special case where we define the label in the slotProps and not as an InputLabel */}
                                                <DatePicker
                                                    desktopModeMediaQuery="@media (min-width: 400px)"
                                                    value={
                                                        selectedFormFilters.endDate ? dayjs(selectedFormFilters.endDate) : null
                                                    }
                                                    onChange={(value) => handleDateRangeChange(value, 'endDate')}
                                                    disableFuture={true}
                                                    disableHighlightToday={true}
                                                    maxDate={dayjs(GREATEST_VALID_END_DATE).startOf('day')}
                                                    slotProps={{
                                                        ...commonDatePickerSlotProps,
                                                        textField: {
                                                            label: 'Route End Date',
                                                            error: !!fieldErrors?.endDateError,
                                                            helperText: fieldErrors?.endDateError,
                                                        },
                                                        actionBar: {
                                                            actions: ['clear'],
                                                        },
                                                    }}
                                                />
                                            </LocalizationProvider>
                                        </FormControl>
                                    </Grid>
                                </Grid>
                                <Grid xl={2} lg={2} md={3} sm={4} xs={6} alignSelf="center">
                                    <Checkbox
                                        icon={CHECKBOX_OUTLINE_ICON}
                                        checkedIcon={CHECKBOX_ICON}
                                        checked={selectedFormFilters.showModified}
                                        onChange={handleShowModifiedChange}
                                    />
                                    Show Modified
                                </Grid>
                                <Grid xl={6} lg={10} md={3} sm={12} xs={6} container spacing={2} className="buttons">
                                    <Box>
                                        <CardActions>
                                            <Button variant="outlined" className="clear-button" onClick={handleResetForm}>
                                                Clear
                                            </Button>
                                            {isFetchingNextPage() ? (
                                                <Button variant="contained" disabled className="submit-button">
                                                    <LoadingIndicator message="Loading" />
                                                </Button>
                                            ) : (
                                                <Button
                                                    variant="contained"
                                                    className="submit-button"
                                                    onClick={applyFiltersAndSearch}
                                                >
                                                    Search
                                                </Button>
                                            )}
                                        </CardActions>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Grid>
                    <Grid xs={12} container spacing={2} className="tabs-container">
                        {/* Using empty grid for spacing */}
                        <Grid md={4} sm={0} xs={0}></Grid>
                        <Grid md={4} sm={12} xs={12}>
                            <Tabs
                                value={currentDataset === Dataset.MissingStops ? 0 : 1}
                                onChange={handleTabChange}
                                centered
                                id="dmt-tabs"
                            >
                                <Tab label="Missing Arrival Times" disableRipple />
                                <Tab label="Late Stops" disableRipple />
                            </Tabs>
                        </Grid>
                        <Grid md={4} sm={12} xs={12} className="total-records-container">
                            {!isFetchingInitData() && (
                                <Box className="record-counts">
                                    <Typography className="label">Showing</Typography>
                                    <Typography className="count">{`${flatTableData?.length}`}</Typography>
                                    <Typography className="label">of</Typography>
                                    <Typography className="count">
                                        {`${fetchedDeliveryData?.pages[fetchedDeliveryData?.pages.length - 1].totalRecords}`}
                                    </Typography>
                                    <Typography className="label">Total Records</Typography>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <CardContent id="dmt-card" className="table-card auto-fill-height">
                    <FmtpTable
                        data={flatTableData ?? []}
                        columns={columns}
                        enableSorting={true}
                        isResizable={true}
                        enableFilters={true}
                        enableExpanding={true}
                        showLoading={true}
                        isFetching={isFetchingInitData()}
                        loadingIndicatorMessage={loadingMessage()}
                        showTable={!!flatTableData}
                        fetchMoreOnBottomReached={fetchMoreOnBottomReached}
                        initialSortingState={initialTableSort}
                        onDataUpdate={handleCellDataUpdate}
                        onFilterChange={handleColumnFilterChange}
                        onSortChange={handleSortChange}
                        isVirtualized={true}
                        virtualizerSettings={{
                            // Estimate of height of each row to help virtualizer render performance
                            estimateSize: () => 61,
                        }}
                        contextMenuColumnNames={['updatedUser']}
                        onContextMenu={handleContextMenuOpen}
                        clearFilters={clearFilters}
                    />
                    <Menu
                        id="undo-menu"
                        // Prevents aria-hidden warning when closing
                        closeAfterTransition={true}
                        open={contextMenu !== null}
                        onBlur={handleContextMenuBlur}
                        disableAutoFocusItem={true}
                        anchorReference="anchorPosition"
                        anchorPosition={
                            contextMenu !== null ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined
                        }
                        PopoverClasses={{ root: 'dmt-popover-root', paper: 'dmt-popover-paper' }}
                        transitionDuration={{ appear: 500, enter: 100, exit: 0 }}
                    >
                        <MenuItem autoFocus={false} onClick={handleShowUndoDialog} ref={undoMenuItem} id="undo-menu-item">
                            {`Undo the ${contextMenu?.userId ? contextMenu?.userId : 'selected'} entry`}
                        </MenuItem>
                    </Menu>
                </CardContent>

                <CardActions className="dmt-save-buttons">
                    <Button
                        variant="outlined"
                        className="cancel-button"
                        onClick={handleCancelChanges}
                        disabled={cellUpdates.count < 1}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        className="submit-button"
                        onClick={handleSaveChanges}
                        disabled={cellUpdates.count < 1}
                    >
                        Save
                    </Button>
                </CardActions>
            </Card>
            <Dialog open={undoDialogOpen} onClose={() => setUndoDialogOpen(false)}>
                <DialogTitle>Undo Entry</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`Are you sure you want to undo the ${pendingUndo?.userId ?? 'selected'} entry?`}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUndoDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={handleConfirmUndo}>
                        Undo
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
